import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RuleException } from "../types";
import { format, formatDistanceToNow } from "date-fns";
import { ArrowRight, User, Clock, Loader2, AlertTriangle } from "lucide-react";

interface RuleExceptionsTableProps {
  ruleExceptions: RuleException[];
  onSelectRuleException: (ruleExceptionId: number) => void;
  onUpdateStatus: (ruleExceptionId: number, status: string) => Promise<boolean>;
}

const getStatusBadge = (status: string) => {
  const statusConfig = {
    "New": { variant: "secondary" as const, className: "bg-slate-100 text-slate-700 hover:bg-slate-200" },
    "Open": { variant: "destructive" as const, className: "bg-red-100 text-red-700 hover:bg-red-200" },
    "In Progress": { variant: "default" as const, className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" },
    "Resolved": { variant: "default" as const, className: "bg-green-100 text-green-700 hover:bg-green-200" },
    "Waived": { variant: "outline" as const, className: "bg-blue-100 text-blue-700 hover:bg-blue-200" },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig["New"];
  
  return (
    <Badge variant={config.variant} className={config.className}>
      {status}
    </Badge>
  );
};

const getRiskLevelBadge = (riskLevel: string) => {
  const riskConfig = {
    "Low": { variant: "secondary" as const, className: "bg-green-100 text-green-700 hover:bg-green-200" },
    "Medium": { variant: "default" as const, className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" },
    "High": { variant: "default" as const, className: "bg-orange-100 text-orange-700 hover:bg-orange-200" },
    "Critical": { variant: "destructive" as const, className: "bg-red-100 text-red-700 hover:bg-red-200" },
  };

  const config = riskConfig[riskLevel as keyof typeof riskConfig] || riskConfig["Low"];
  
  return (
    <Badge variant={config.variant} className={`${config.className} text-xs`}>
      {riskLevel}
    </Badge>
  );
};

export const ExceptionsTable: React.FC<RuleExceptionsTableProps> = ({
  ruleExceptions,
  onSelectRuleException,
  onUpdateStatus,
}) => {
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  const handleStatusUpdate = async (ruleExceptionId: number, newStatus: string) => {
    setUpdatingStatus(ruleExceptionId);
    try {
      await onUpdateStatus(ruleExceptionId, newStatus);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      // If less than 24 hours ago, show relative time
      if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true });
      }
      
      // Otherwise show formatted date
      return format(date, "MMM d");
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <div className="w-full overflow-hidden">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
            <TableHead className="font-semibold text-slate-700 min-w-0">Description</TableHead>
            <TableHead className="font-semibold text-slate-700 w-24 hidden sm:table-cell">Rule Key</TableHead>
            <TableHead className="font-semibold text-slate-700 w-24 hidden md:table-cell">Risk Level</TableHead>
            <TableHead className="font-semibold text-slate-700 w-32">Status</TableHead>
            <TableHead className="font-semibold text-slate-700 w-32 hidden lg:table-cell">Assigned</TableHead>
            <TableHead className="font-semibold text-slate-700 w-20 hidden xl:table-cell">Updated</TableHead>
            <TableHead className="font-semibold text-slate-700 w-20 text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ruleExceptions.length > 0 ? (
            ruleExceptions.map((exception, index) => (
              <TableRow 
                key={exception.ruleExceptionId} 
                className="border-slate-100 hover:bg-slate-50/30 transition-colors group"
              >
                <TableCell className="py-3 min-w-0">
                  <div className="space-y-1">
                    <div className="font-medium text-slate-800 leading-tight text-sm">
                      {exception.shortDescription}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <p className="text-xs text-slate-600 line-clamp-1">
                        {exception.description}
                      </p>
                      <div className="sm:hidden">
                        <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-700">
                          {exception.ruleKey}
                        </code>
                      </div>
                    </div>
                    {/* Mobile: Show risk level, assigned to and updated info */}
                    <div className="flex items-center gap-3 text-xs text-slate-500 lg:hidden">
                      <div className="md:hidden">
                        {getRiskLevelBadge(exception.riskLevel)}
                      </div>
                      {exception.assignedTo && (
                        <div className="flex items-center gap-1 lg:hidden">
                          <User className="h-3 w-3" />
                          <span className="truncate max-w-20">{exception.assignedTo}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 xl:hidden">
                        <Clock className="h-3 w-3" />
                        <span>{formatDateTime(exception.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </TableCell>
                
                <TableCell className="py-3 hidden sm:table-cell">
                  <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono text-slate-700 block">
                    {exception.ruleKey}
                  </code>
                </TableCell>
                
                <TableCell className="py-3 hidden md:table-cell">
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className="h-3 w-3 text-slate-400" />
                    {getRiskLevelBadge(exception.riskLevel)}
                  </div>
                </TableCell>
                
                <TableCell className="py-3">
                  <Select
                    value={exception.status}
                    onValueChange={(newStatus) => handleStatusUpdate(exception.ruleExceptionId, newStatus)}
                    disabled={updatingStatus === exception.ruleExceptionId}
                  >
                    <SelectTrigger className="w-full border-slate-200 focus:border-slate-400 h-8 text-xs">
                      <SelectValue>
                        <div className="flex items-center space-x-1">
                          {updatingStatus === exception.ruleExceptionId ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : null}
                          <span className="truncate">
                            {exception.status}
                          </span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Resolved">Resolved</SelectItem>
                      <SelectItem value="Waived">Waived</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                
                <TableCell className="py-3 hidden lg:table-cell">
                  <div className="flex items-center space-x-2">
                    {exception.assignedTo ? (
                      <>
                        <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-700 truncate max-w-24">
                          {exception.assignedTo}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-slate-400 italic">
                        Unassigned
                      </span>
                    )}
                  </div>
                </TableCell>
                
                <TableCell className="py-3 hidden xl:table-cell">
                  <div className="flex items-center space-x-1 text-slate-600">
                    <Clock className="h-3 w-3 flex-shrink-0" />
                    <span className="text-xs" title={format(new Date(exception.updatedAt), "PPpp")}>
                      {formatDateTime(exception.updatedAt)}
                    </span>
                  </div>
                </TableCell>
                
                <TableCell className="text-right py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSelectRuleException(exception.ruleExceptionId)}
                    className="text-slate-600 hover:text-slate-800 hover:bg-slate-100 group-hover:bg-slate-100 transition-colors h-8 px-2 text-xs"
                  >
                    View
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={7}
                className="text-center py-12 text-slate-500"
              >
                No rule exceptions found with the current filters
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
